import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { 
  CheckCircle2, 
  Circle, 
  X,
  Briefcase,
  CheckSquare,
  MessageSquare,
  AtSign,
  Calendar,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Notification {
  id: string;
  type: 'project_assignment' | 'task_assignment' | 'comment' | 'mention' | 'deadline_reminder';
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  url?: string;
}

interface NotificationItemProps {
  notification: Notification;
  onClose?: () => void;
}

const typeIcons = {
  project_assignment: Briefcase,
  task_assignment: CheckSquare,
  comment: MessageSquare,
  mention: AtSign,
  deadline_reminder: Calendar,
};

const typeColors = {
  project_assignment: 'text-blue-500',
  task_assignment: 'text-green-500',
  comment: 'text-purple-500',
  mention: 'text-orange-500',
  deadline_reminder: 'text-red-500',
};

export const NotificationItem = ({ notification, onClose }: NotificationItemProps) => {
  const navigate = useNavigate();
  const Icon = typeIcons[notification.type] || Circle;
  const iconColor = typeColors[notification.type] || 'text-muted-foreground';

  const handleClick = () => {
    if (notification.url) {
      navigate(notification.url);
    }
    if (onClose) {
      onClose();
    }
    // TODO: Mark as read
  };

  const markAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO: Implement mark as read
    console.log('Mark as read:', notification.id);
  };

  const dismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO: Implement dismiss
    console.log('Dismiss:', notification.id);
  };

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-4 hover:bg-muted/50 transition-colors cursor-pointer",
        !notification.read && "bg-muted/30"
      )}
      onClick={handleClick}
    >
      <div className={cn("flex-shrink-0 mt-0.5", iconColor)}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className={cn(
              "text-sm font-medium",
              !notification.read && "font-semibold"
            )}>
              {notification.title}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {notification.message}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {!notification.read && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={markAsRead}
              >
                <CheckCircle2 className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={dismiss}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

